import { useLayoutEffect, useMemo, useRef } from 'react';
import { useStrings } from '../../tenant/TenantProvider';

const WEEKS = 53;
const DAY_MS = 86_400_000;
const MONTH_FORMAT = new Intl.DateTimeFormat(undefined, { month: 'short' });
const DAY_FORMAT = new Intl.DateTimeFormat(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });

export function localDayKey(value) {
  const date = new Date(value);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function levelFor(count) {
  if (count === 0) return 0;
  if (count === 1) return 1;
  if (count <= 3) return 2;
  if (count <= 6) return 3;
  return 4;
}

export function ActivityHeatmap({ counts }) {
  const t = useStrings();
  const scrollRef = useRef(null);

  const { weeks, months } = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const start = new Date(today.getTime() - (today.getDay() + (WEEKS - 1) * 7) * DAY_MS);
    start.setHours(0, 0, 0, 0);
    const columns = [];
    const labels = [];
    for (let week = 0; week < WEEKS; week += 1) {
      const days = [];
      for (let day = 0; day < 7; day += 1) {
        const date = new Date(start.getFullYear(), start.getMonth(), start.getDate() + week * 7 + day);
        const key = localDayKey(date);
        const count = counts[key] ?? 0;
        days.push({ key, date, count, future: date > today });
        if (date.getDate() === 1 && date <= today) labels.push({ week, label: MONTH_FORMAT.format(date) });
      }
      columns.push(days);
    }
    return { weeks: columns, months: labels };
  }, [counts]);

  useLayoutEffect(() => {
    const node = scrollRef.current;
    if (node) node.scrollLeft = node.scrollWidth;
  }, [weeks]);

  return (
    <div className="activity">
      <div ref={scrollRef} className="activity__scroll">
        <div className="activity__months" style={{ '--weeks': WEEKS }} aria-hidden="true">
          {months.map((month) => (
            <span key={`${month.week}-${month.label}`} style={{ gridColumnStart: month.week + 1 }}>
              {month.label}
            </span>
          ))}
        </div>
        <div className="heatmap activity__grid" style={{ '--weeks': WEEKS }} role="img" aria-label={t('profile.activityLabel')}>
          {weeks.flatMap((days, week) =>
            days.map((day) => (
              <span
                key={day.key}
                className={`heatmap__cell${day.future ? ' is-future' : ''}`}
                data-level={levelFor(day.count)}
                style={{ '--i': week }}
                title={day.future ? undefined : t('profile.activityDay', { count: day.count, date: DAY_FORMAT.format(day.date) })}
              />
            ))
          )}
        </div>
      </div>
      <div className="heatmap-legend" aria-hidden="true">
        <span>{t('profile.less')}</span>
        {[0, 1, 2, 3, 4].map((level) => (
          <span key={level} className="heatmap__cell" data-level={level} />
        ))}
        <span>{t('profile.more')}</span>
      </div>
    </div>
  );
}
